import React, { useContext } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch, useSelector, shallowEqual } from "react-redux";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { hideCart } from "../../actions/ShopActions";
import {
  getOrderedLineItems,
  getCheckoutState,
  getAllProducts,
} from "../../utils/reduxSelectors";
import {
  isInCart,
  getLineItemCustomAttribute,
  getCartRecommendedProducts,
  getLineItemDiscountsApplied,
} from "../../utils/shop";
import { priceConstructor } from "../../utils/general";
import { isCloudPillowGWPEnabled } from "../../utils/promos";
import { isRestMiniGWPEnabled } from "../../utils/promos";

// Components
import Backdrop from "../Atoms/Backdrop";
import Button from "../Atoms/Button";
import IconButton from "../Atoms/IconButton";
import Image from "../Atoms/Image";
import Paragraph from "../Atoms/Paragraph";
import ButtonGroup from "../Molecules/ButtonGroup";
import LineItem from "../Molecules/LineItem";
import LineItemGiftWithPurchase from "../Molecules/LineItemGiftWithPurchase";
import ShopifyRecommendedProducts from "../Molecules/ShopifyRecommendedProducts";
import { ImageGalleryProvider } from "../Molecules/ImageGallery";
import CheckoutButton from "../Utils/CheckoutButton";
import ContentPlaceholder from "../Utils/ContentPlaceholder";
import { TrackingContext } from "../Utils/TrackingWrapper";

// Stylesheets
import "./Cart.scss";
import { EVENT_PROPERTIES_VALUES } from "../../types/Amplitude";

// Constants
import { IMGIX_BASE_PATH } from "../../utils/constants";
import { EMPTY_CART_SHOP_LINKS } from "../../utils/shopConstants";
import {
  CART_DESCRIPTION_KEY,
  PROMOTIONAL_MESSAGE_KEY,
  PRODUCT_IS_VARIANT_KEY,
  HATCH_CLOUD_PILLOW_KEY,
  REST_MINI_KEY,
  ALT_LINE_ITEM_DISPLAY_NAME_KEY,
} from "../../utils/productConstants";
const IS_CLOUD_PILLOW_PROMO_ENABLED = isCloudPillowGWPEnabled();
const IS_REST_MINI_PROMO_ENABLED = isRestMiniGWPEnabled();

const onShopButtonClick =
  ({ trackShopButtonClick, trackCloseCart, closeCart, pageUrl, action }) =>
  () => {
    const trackingProps = {
      pageUrl,
      action,
      sectionName: EVENT_PROPERTIES_VALUES.Cart,
    };

    trackShopButtonClick(trackingProps);
    trackCloseCart(trackingProps);

    closeCart();
  };

const ShopButton = ({ link, product, action }) => {
  const {
    trackingActions: { trackShopButtonClick, trackCloseCart },
    pageUrl,
  } = useContext(TrackingContext);
  const dispatch = useDispatch();

  return (
    <Button
      buttonType="secondary"
      className="shop_button"
      onClick={onShopButtonClick({
        closeCart: () => dispatch(hideCart()),
        pageUrl,
        trackShopButtonClick,
        trackCloseCart,
        action,
      })}
      linkProps={{ to: link }}
    >
      Shop {product}
    </Button>
  );
};

ShopButton.propTypes = {
  link: PropTypes.string.isRequired,
  product: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  action: PropTypes.string.isRequired,
};

const renderCartContents = (orderedLineItems, addingToCart) => {
  if (orderedLineItems.length === 0 && !addingToCart) {
    return (
      // Empty cart
      <div className="empty_cart">
        <Paragraph className="empty_cart_message h5_text">
          Your cart is empty.
        </Paragraph>

        <ButtonGroup layout="stacked">
          {EMPTY_CART_SHOP_LINKS.map((item, idx) => (
            <ShopButton
              key={idx}
              link={item.link}
              product={item.product}
              action={item.action}
            />
          ))}
        </ButtonGroup>
      </div>
    );
  }

  return <ActiveCart addingToCart={addingToCart} />;
};

export const constructLineItemData = (lineItem) => ({
  // For checkout
  id: lineItem.id, // Comes from checkout line item
  // For Cart Line Item rendering
  price: parseFloat(lineItem.variant.price), // Comes from checkout line item
  variantId: lineItem.variant.id, // Comes from checkout line item
  quantity: lineItem.quantity, // Comes from checkout line item
  image: lineItem.variant.image, // Comes from checkout line item
  description: getLineItemCustomAttribute(lineItem, CART_DESCRIPTION_KEY),
  promotionalMessage: getLineItemCustomAttribute(
    lineItem,
    PROMOTIONAL_MESSAGE_KEY
  ),
  displayName:
    getLineItemCustomAttribute(lineItem, ALT_LINE_ITEM_DISPLAY_NAME_KEY) ||
    lineItem.title, // Comes from checkout line item
  // For tracking
  productName: lineItem.title,
  discounts: lineItem.discountAllocations,
  variantName: getLineItemCustomAttribute(lineItem, PRODUCT_IS_VARIANT_KEY)
    ? lineItem.variant.title
    : null, // Comes from checkout line item
  variantTitle: lineItem.variant.title, // Comes from checkout line item
  handle: lineItem.variant.product.handle, // Comes from checkout line item
  sku: lineItem.variant.sku, // Comes from checkout line item
});

const renderLineItemComponent = ({ pillowVariantId, lineItemData, index }) => {
  const isGiftWithPurchase =
    (IS_CLOUD_PILLOW_PROMO_ENABLED || IS_REST_MINI_PROMO_ENABLED) &&
    pillowVariantId === lineItemData.variantId;
  const hasDiscountsApplied = !isEmpty(
    getLineItemDiscountsApplied(lineItemData)
  );

  return isGiftWithPurchase && hasDiscountsApplied ? (
    <LineItemGiftWithPurchase
      key={index}
      gwpProductKey={
        IS_REST_MINI_PROMO_ENABLED ? REST_MINI_KEY : HATCH_CLOUD_PILLOW_KEY
      }
      lineItem={lineItemData}
    />
  ) : (
    <LineItem key={index} lineItem={lineItemData} />
  );
};

export const CartLineItems = ({ shopifyLineItems }) => {
  // To preserve line item order, iterate over orderedLineItems
  // and then use the line item data returned from Shopify to render
  // each line item component
  const allProducts = useSelector(
    (state) => getAllProducts(state),
    shallowEqual
  );

  //This is used to push specific products to the top of the cart
  let filteredShopifyLineItems = [];
  for (let i = 0; i < shopifyLineItems.length; i++) {
    if (shopifyLineItems[i].title === "Rest Mini") {
      filteredShopifyLineItems.unshift(shopifyLineItems[i]);
    } else {
      filteredShopifyLineItems.push(shopifyLineItems[i]);
    }
  }

  //update this to filteredShopifyLineItems and update the condition with the title of product you want prioritized on top if you want to control the order of items in the cart otherwise use shopifyLineItems
  return shopifyLineItems.map((lineItem, index) => {
    if (lineItem) {
      return renderLineItemComponent({
        pillowVariantId: allProducts[REST_MINI_KEY]
          ? allProducts[REST_MINI_KEY].variants[0].id
          : allProducts[HATCH_CLOUD_PILLOW_KEY]
          ? allProducts[HATCH_CLOUD_PILLOW_KEY].variants[0].id
          : null,
        variantId: lineItem.variant.id,
        lineItemData: constructLineItemData(lineItem),
        index,
      });
    }

    return null;
  });
};

const renderPlaceholder = (lineItems, addingToCart) => {
  /* Don't show the loader if:
    - Nothing is being added to the cart
    - The item being added is already in the cart
  */
  if (addingToCart && !isInCart(addingToCart, lineItems)) {
    return (
      <li className="line_item is_adding">
        <ContentPlaceholder ariaLabel="Adding to cart..." />
      </li>
    );
  }

  return null;
};

const renderRecommendedProduct = (recommendedProduct) => {
  if (isEmpty(recommendedProduct)) {
    return null;
  }

  return (
    <ImageGalleryProvider>
      <ShopifyRecommendedProducts recommendedProduct={recommendedProduct} />
    </ImageGalleryProvider>
  );
};

export const ActiveCart = ({ addingToCart }) => {
  const allProducts = useSelector(
    (state) => getAllProducts(state),
    shallowEqual
  );
  const { lineItems, subtotalPrice } = useSelector(
    (state) => getCheckoutState(state),
    shallowEqual
  );
  const orderedLineItems = useSelector(
    (state) => getOrderedLineItems(state),
    shallowEqual
  );
  const [recommendedProduct] = getCartRecommendedProducts({
    lineItems,
    allProducts,
  });

  return (
    <>
      <ul className="cart_line_items unstyled_list">
        {renderPlaceholder(orderedLineItems, addingToCart)}

        <CartLineItems shopifyLineItems={lineItems} />
      </ul>
      <footer className="cart_footer">
        <div className="subtotal_info">
          <span className="h6_text">Subtotal</span>
          <span className="h6_text subtotal_price">
            {priceConstructor({ price: subtotalPrice })}
          </span>
        </div>
        <Paragraph className="caption_secondary guarantee">
          Happiness guaranteed.
        </Paragraph>

        <CheckoutButton
          trackingProps={{ sectionName: EVENT_PROPERTIES_VALUES.Cart }}
        />
      </footer>

      {renderRecommendedProduct(recommendedProduct)}
    </>
  );
};

ActiveCart.propTypes = {
  addingToCart: PropTypes.string,
};

const closeCartIconAction =
  ({ closeCart, trackCloseCart, pageUrl }) =>
  () => {
    trackCloseCart({
      pageUrl,
      action: EVENT_PROPERTIES_VALUES.Close_Cart_Icon_Clicked,
      sectionName: EVENT_PROPERTIES_VALUES.Cart,
    });

    closeCart();
  };

export const CloseCartIcon = ({ closeCart }) => {
  const {
    trackingActions: { trackCloseCart },
    pageUrl,
  } = useContext(TrackingContext);

  return (
    <IconButton
      onClick={closeCartIconAction({
        closeCart,
        trackCloseCart,
        pageUrl,
      })}
      ariaLabel="Close Cart"
      className="cart_close"
    >
      <Image src={`${IMGIX_BASE_PATH}/close-cart-icon.svg`} alt="Close cart" />
    </IconButton>
  );
};

CloseCartIcon.propTypes = {
  closeCart: PropTypes.func,
};

export const Cart = ({
  closeCart,
  cartOpen,
  addingToCart,
  orderedLineItems,
}) => {
  const className = classNames("cart", { is_open: cartOpen });

  return (
    <>
      <Backdrop
        isVisible={cartOpen}
        handleHide={closeCart}
        ariaLabel="Close Cart"
      />

      <div className={className}>
        <header className="cart_header">
          <h2 className="heading caption_primary">Your cart</h2>

          <CloseCartIcon closeCart={closeCart} />
        </header>
        {renderCartContents(orderedLineItems, addingToCart)}
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    cartOpen: state.shop.cartOpen,
    addingToCart: state.shop.addingToCart,
    orderedLineItems: getOrderedLineItems(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    closeCart: () => dispatch(hideCart()),
  };
};

Cart.propTypes = {
  closeCart: PropTypes.func.isRequired,
  cartOpen: PropTypes.bool,
  addingToCart: PropTypes.string,
  orderedLineItems: PropTypes.arrayOf(PropTypes.string),
};

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
